import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Eye,
  Copy,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { buildLeadIngestUrl, leadIngestBaseUrl } from './leadIngestUrl';
import { useAdConnections } from '../../hooks/useAdConnections';
import { useCrmConnection } from '../../hooks/useCrmConnection';
import { useLeads } from '../../hooks/useLeads';
import { useLeadCaptureOnboarding } from '../../hooks/useLeadCaptureOnboarding';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Mockup §2: Connected Lead Sources. 10 source cards + webhook setup card.
//
// Data model for Wave 2: static source catalog + live connection state
// from useAdConnections + useCrmConnection. Month-to-date lead counts
// land in Wave 3 once the Firestore aggregate queries are wired. Cards
// show "—" for counts in the meantime rather than invented numbers.

type SourceType =
  | 'Direct Webhook'
  | 'Direct Webhook (Meta)'
  | 'API Connector'
  | 'API Connector (Optional)'
  | 'Manual Entry';

interface SourceDef {
  id: string;
  name: string;
  type: SourceType;
  iconText: string;
  iconBg: string;
  description: string;
}

const SOURCES: SourceDef[] = [
  {
    id: 'wordpress_gravity',
    name: 'WordPress (Gravity Forms)',
    type: 'Direct Webhook',
    iconText: 'WP',
    iconBg: '#21759b',
    description:
      'Forms on your site fire webhooks directly to Valutten on every submission. UTM parameters captured automatically.',
  },
  {
    id: 'facebook_lead_ads',
    name: 'Facebook Lead Ads',
    type: 'Direct Webhook',
    iconText: 'f',
    iconBg: '#1877f2',
    description:
      'Meta Lead Ads webhook subscription. Leads arrive in real-time when someone fills out a Facebook / Instagram lead form.',
  },
  {
    id: 'google_ads',
    name: 'Google Ads Lead Forms',
    type: 'Direct Webhook',
    iconText: 'G',
    iconBg: '#4285f4',
    description:
      'Google Ads lead form extensions POST directly to Valutten. Campaign, ad group, and keyword captured.',
  },
  {
    id: 'instagram_lead_ads',
    name: 'Instagram Lead Ads',
    type: 'Direct Webhook (Meta)',
    iconText: 'IG',
    iconBg: '#e4405f',
    description:
      'Same Meta webhook as Facebook. Instagram lead ads flow through the same pipeline with platform tagged.',
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    type: 'API Connector (Optional)',
    iconText: 'H',
    iconBg: '#ff7a59',
    description:
      'Optional: pull contacts and deals from HubSpot if you use it for marketing automation. Daily sync or real-time webhooks.',
  },
  {
    id: 'manual',
    name: 'Manual / Referral',
    type: 'Manual Entry',
    iconText: '+',
    iconBg: '#4b5563',
    description:
      'Walk-in, phone, and referral partner leads entered manually by brokers via the Lead Inbox.',
  },
  {
    id: 'monday',
    name: 'Monday.com (Property)',
    type: 'API Connector',
    iconText: 'M',
    iconBg: '#6c5ce7',
    description:
      'Pull property deal data from Monday boards. Track cross-department pipeline from property division into Valutten.',
  },
  {
    id: 'smokeball',
    name: 'Smokeball (Legal)',
    type: 'API Connector',
    iconText: 'S',
    iconBg: '#00b894',
    description:
      'Pull legal case status from Smokeball. Track conveyancing progress alongside settlements. API access requires invitation.',
  },
  {
    id: 'mycrm',
    name: 'MyCRM (Finance)',
    type: 'API Connector',
    iconText: 'MC',
    iconBg: '#e17055',
    description:
      'Pull deals, advisers, and pipeline from MyCRM (Loan Market). Daily sync of settled deals for trail leakage detection.',
  },
  {
    id: 'mycrm_notify',
    name: 'MyCRM Notify',
    type: 'API Connector',
    iconText: 'MN',
    iconBg: '#d63031',
    description:
      'Pull notification and lodgement data from MyCRM Notify. Same LMG API ecosystem as MyCRM.',
  },
];

const WEBHOOK_STEPS: Array<{ title: string; body: string }> = [
  {
    title: 'Valutten generates a unique webhook URL per source',
    body: 'Each lead source gets its own endpoint with a secret token for verification. Tokens rotate from the Integrations page.',
  },
  {
    title: "Paste the URL into your form tool's webhook settings",
    body: 'Gravity Forms: Settings → Webhooks → Add New. Facebook: Business Settings → Integrations → Lead Access. Google Ads: Lead form extension → Lead delivery → Webhook.',
  },
  {
    title: 'Leads flow in automatically with full attribution',
    body: 'UTM parameters, source, medium, campaign, content, and term are all captured from the URL. No middleware, no Zapier.',
  },
];

// Map card IDs to the lead `source` field values actually written by the
// pipeline. Used to look up per-source lead counts + token actions.
const SOURCE_TOKEN_KEY: Record<string, string> = {
  wordpress_gravity: 'wordpress_gravity',
  facebook_lead_ads: 'facebook_lead_ads',
  google_ads: 'google_ads',
  instagram_lead_ads: 'instagram_lead_ads',
  hubspot: 'hubspot',
  manual: 'manual',
};

// Map each source CARD id to the pipeline `source` enum value(s) that feed
// its count. The pipeline writes a FIXED taxonomy (see leadIngest.ts
// LEAD_SOURCES): website_form, google_ads, meta_ads, facebook_lead_ads,
// manual, csv_import, referral, other. The card ids are a richer product
// vocabulary, so several cards fan into one-or-more enum values and several
// API-connector cards have no backing enum yet.
//
// Distinction the UI relies on:
//   • Card present here with ≥1 key  → real count (may be 0 this period).
//   • Card present here with []       → no pipeline source yet → render "—"
//     (honest: this channel can't produce a count, vs. a fake "0").
const CARD_TO_SOURCE_KEYS: Record<string, string[]> = {
  // WordPress / Gravity Forms submissions land as website_form.
  wordpress_gravity: ['website_form'],
  // Meta Lead Ads (one connection) covers both FB + IG; the pipeline tags
  // them as facebook_lead_ads or the broader meta_ads — sum both so neither
  // card under-counts (mirrors connectedMap's shared-Meta logic below).
  facebook_lead_ads: ['facebook_lead_ads', 'meta_ads'],
  instagram_lead_ads: ['facebook_lead_ads', 'meta_ads'],
  google_ads: ['google_ads'],
  // "Manual / Referral" card — both manual entry and referral-partner leads.
  manual: ['manual', 'referral'],
  // API-connector roadmap cards — no pipeline source emits these yet.
  hubspot: [],
  monday: [],
  smokeball: [],
  mycrm: [],
  mycrm_notify: [],
};

/**
 * A card is PLANNED when no pipeline source emits it yet, i.e. it has no
 * mapped `source` enum value in CARD_TO_SOURCE_KEYS. Derived rather than
 * listed separately so the badge cannot drift from the mapping that decides
 * whether the card can ever show a count.
 *
 * These cards were presented identically to live sources, so the page read
 * as "nine sources, three connected" when several of the nine cannot be
 * connected at all.
 */
function isPlannedSource(cardId: string): boolean {
  return (CARD_TO_SOURCE_KEYS[cardId]?.length ?? 0) === 0;
}

const SourceCard: React.FC<{
  def: SourceDef;
  connected: boolean;
  planned?: boolean;
  leadCountLabel: string;
  onGenerateToken?: () => void;
  tokenBusy?: boolean;
  tokenUrl?: string | null;
  onCopy?: () => void;
}> = ({ def, connected, planned, leadCountLabel, onGenerateToken, tokenBusy, tokenUrl, onCopy }) => (
  <div
    className={`rounded-lg border ${
      connected
        ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
        : 'border-gray-700 bg-gray-800/40'
    } p-5`}
  >
    <div className="flex items-start gap-3 mb-3">
      <div
        className="w-10 h-10 rounded flex items-center justify-center font-bold text-white flex-shrink-0"
        style={{ background: def.iconBg }}
      >
        {def.iconText}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-white">{def.name}</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">{def.type}</p>
      </div>
      {planned ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
          Planned
        </span>
      ) : connected ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
          <Circle className="w-2 h-2 fill-emerald-400" />
          Connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-500/15 text-gray-300 border border-gray-500/30">
          Not connected
        </span>
      )}
    </div>
    <p className="text-xs text-gray-400 mb-3 leading-relaxed">
      {def.description}
    </p>
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-gray-500">
        Leads (30d): <span className="text-gray-300 font-semibold">{leadCountLabel}</span>
      </span>
      {connected ? (
        <span className="text-emerald-400 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Active
        </span>
      ) : null}
    </div>
    {tokenUrl && (
      <div className="mt-3 pt-3 border-t border-gray-700/50 space-y-1.5">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">
          Per-source webhook URL
        </div>
        <div className="flex items-center gap-1.5">
          <code className="flex-1 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-[10px] font-mono text-gray-300 truncate">
            {tokenUrl}
          </code>
          <button
            type="button"
            onClick={onCopy}
            className="p-1 rounded hover:bg-gray-700 text-gray-400"
            title="Copy"
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
      </div>
    )}
    {onGenerateToken && (
      <div className="mt-3">
        <button
          type="button"
          onClick={onGenerateToken}
          disabled={tokenBusy}
          className="px-2.5 py-1 rounded bg-gray-700 hover:bg-gray-600 text-[11px] text-white disabled:opacity-50"
        >
          {tokenBusy
            ? 'Generating…'
            : tokenUrl
              ? 'Rotate token'
              : 'Generate source token'}
        </button>
      </div>
    )}
  </div>
);

const LeadCaptureSourcesPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;

  const { connections: adConnections } = useAdConnections(companyId);
  const { connections: crmConnections } = useCrmConnection(companyId);
  const { getLeadCountsBySource } = useLeads(companyId);
  const { generateSourceToken } = useLeadCaptureOnboarding(companyId);

  // Live per-source counts (last 30 days). Renders dashes until the first
  // fetch lands so empty cards don't invent zeroes.
  const [sourceCounts, setSourceCounts] = useState<Record<string, number>>({});
  const [countsLoaded, setCountsLoaded] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getLeadCountsBySource(30)
      .then((r) => {
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const row of r.counts) map[row.source] = row.count;
        setSourceCounts(map);
        setCountsLoaded(true);
      })
      .catch((err) =>
        console.warn('[Sources] getLeadCountsBySource failed', err)
      );
    return () => {
      cancelled = true;
    };
  }, [companyId, getLeadCountsBySource]);

  // Per-source token state. Generated on demand; stored in local state for
  // the session so the URL stays visible after the one-time reveal.
  const [sourceTokens, setSourceTokens] = useState<Record<string, string>>({});
  const [tokenBusy, setTokenBusy] = useState<Record<string, boolean>>({});

  const ingestBase = leadIngestBaseUrl();

  const handleGenerateSourceToken = async (sourceKey: string) => {
    setTokenBusy((prev) => ({ ...prev, [sourceKey]: true }));
    try {
      const token = await generateSourceToken(sourceKey);
      setSourceTokens((prev) => ({ ...prev, [sourceKey]: token }));
    } catch (err) {
      console.error('[Sources] generateSourceToken failed', err);
    } finally {
      setTokenBusy((prev) => ({ ...prev, [sourceKey]: false }));
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  // Derive connection state for each source from the two live hooks. Cards
  // without a matching connection source default to Not Connected. The
  // mapping is explicit rather than fuzzy — if a new provider is added to
  // either hook we wire it here deliberately, not by accident.
  const connectedMap = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    const hasMetaAds = adConnections.some(
      (c) => c.provider === 'meta_ads' && c.status === 'active'
    );
    const hasGoogleAds = adConnections.some(
      (c) => c.provider === 'google_ads' && c.status === 'active'
    );
    const hasMyCrm = crmConnections.some((c) => c.status === 'active');
    map['facebook_lead_ads'] = hasMetaAds;
    map['instagram_lead_ads'] = hasMetaAds; // same Meta connection covers both
    map['google_ads'] = hasGoogleAds;
    map['mycrm'] = hasMyCrm;
    // WordPress / Manual are "connected" whenever the public webhook is
    // enabled for the company — wired in Wave 3 when per-source token
    // state is exposed by leadCaptureAction.listSources.
    map['wordpress_gravity'] = false;
    map['manual'] = true; // manual entry is always available
    map['hubspot'] = false;
    map['monday'] = false;
    map['smokeball'] = false;
    map['mycrm_notify'] = false;
    return map;
  }, [adConnections, crmConnections]);

  const connectedCount = Object.values(connectedMap).filter(Boolean).length;
  // Denominator counts only sources that CAN be connected. Counting the
  // planned API-connector cards made the page read "3 of 9 connected" when
  // several of the nine are not connectable at all.
  const liveSourceCount = SOURCES.filter((d) => !isPlannedSource(d.id)).length;

  const webhookUrl = ingestBase;
  const handleCopyWebhook = () => handleCopy(webhookUrl);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Eye}
        title="Connected Lead Sources"
        subtitle={`Manage webhook endpoints and API connections. Each source gets a unique URL. ${connectedCount} of ${liveSourceCount} available sources connected.`}
      />

      {/* Source cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {SOURCES.map((def) => {
          const tokenKey = SOURCE_TOKEN_KEY[def.id];
          // Sum the pipeline `source` enum values mapped to this card. A card
          // with no mapped enum (API-connector roadmap) keeps the honest "—";
          // a mapped card with no leads this window shows "0".
          const sourceKeys = CARD_TO_SOURCE_KEYS[def.id];
          const hasMappedSource = (sourceKeys?.length ?? 0) > 0;
          const count = hasMappedSource
            ? sourceKeys!.reduce((n, k) => n + (sourceCounts[k] ?? 0), 0)
            : undefined;
          const label =
            !hasMappedSource || !countsLoaded
              ? '—'
              : (count ?? 0).toLocaleString('en-AU');
          const token = tokenKey ? sourceTokens[tokenKey] : undefined;
          // Company id included: without `c` leadIngest 400s on the very
          // URL this card tells the integrator to paste.
          const tokenUrl = token && companyId
            ? buildLeadIngestUrl({ companyId, token, source: tokenKey })
            : null;
          const planned = isPlannedSource(def.id);
          return (
            <SourceCard
              key={def.id}
              def={def}
              connected={connectedMap[def.id] ?? false}
              planned={planned}
              leadCountLabel={label}
              // No token for a PLANNED card. The token is keyed on a pipeline
              // `source` value, and a planned card has none, so the button
              // minted a credential for a source that can never post a lead.
              onGenerateToken={
                tokenKey && !planned
                  ? () => handleGenerateSourceToken(tokenKey)
                  : undefined
              }
              tokenBusy={tokenKey && !planned ? tokenBusy[tokenKey] : false}
              tokenUrl={planned ? null : tokenUrl}
              onCopy={
                tokenUrl && !planned ? () => handleCopy(tokenUrl) : undefined
              }
            />
          );
        })}
      </div>

      {/* Webhook setup guide */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
        <h3 className="text-sm font-semibold text-white mb-4">
          Webhook setup — how it works
        </h3>
        <ol className="space-y-4">
          {WEBHOOK_STEPS.map((step, idx) => (
            <li key={step.title} className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-bold inline-flex items-center justify-center flex-shrink-0">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">
                  {step.title}
                </div>
                <div className="text-xs text-gray-400 mt-1 leading-relaxed">
                  {step.body}
                </div>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 pt-4 border-t border-gray-700/50">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
            Example webhook URL
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-xs font-mono text-gray-300 truncate">
              {webhookUrl}
            </code>
            <button
              type="button"
              onClick={handleCopyWebhook}
              className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            All sources currently share this one company-level webhook token,
            generated from the Inbox page. Per-source and per-office tokens
            are planned.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureSourcesPage;
